import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Card,
  Flex,
  Button,
  TextField,
  Select,
  IconButton,
  Callout,
  Badge,
  SegmentedControl,
  Separator,
} from '@radix-ui/themes';
import {
  MixerHorizontalIcon,
  ArrowLeftIcon,
  InfoCircledIcon,
  MagicWandIcon,
  Pencil1Icon,
} from '@radix-ui/react-icons';
import { useCreateSignalMutation } from '../../graphql/mutations/CreateSignal.generated';
import { GetSignalsDocument } from '../../graphql/queries/GetSignals.generated';
import {
  SignalType,
  Fingerprint,
  TransactionFingerprintInput,
  TransactionFingerprint,
  TransactionField,
  GeneratedSignalResult,
  FingerprintValueType,
  LogFingerprint,
  LogField,
  BlockFingerprint,
  BlockField,
} from '../../types';
import { SignalEditor, AISignalPrompt } from '@/components';

type CreateMode = 'ai' | 'manual';

// Convert AI-generated fingerprints to form fingerprints
const convertAIFingerprints = (
  generated: GeneratedSignalResult,
): Fingerprint[] => {
  return generated.fingerprints.map((fp) => {
    const field = fp.field.toUpperCase();
    const operation = fp.operator;

    // Determine value type based on field and signal type
    let valueType = FingerprintValueType.String;
    if (
      field === 'VALUE' ||
      field === 'GAS' ||
      field === 'GASPRICE' ||
      field === 'GASLIMIT' ||
      field === 'GASUSED' ||
      field === 'BASEFEEPERGAS'
    ) {
      // Check if it's already a monetary value object
      if (
        typeof fp.value === 'object' &&
        fp.value !== null &&
        'amount' in fp.value
      ) {
        valueType = FingerprintValueType.MonetaryValue;
      } else {
        valueType = FingerprintValueType.Number;
      }
    } else if (
      field === 'BLOCKNUMBER' ||
      field === 'NUMBER' ||
      field === 'TIMESTAMP' ||
      field === 'DIFFICULTY' ||
      field === 'TRANSACTIONINDEX' ||
      field === 'LOGINDEX'
    ) {
      valueType = FingerprintValueType.Number;
    } else if (
      field === 'TOPIC0' ||
      field === 'TOPIC1' ||
      field === 'TOPIC2' ||
      field === 'TOPIC3' ||
      field === 'BLOCKHASH' ||
      field === 'TRANSACTIONHASH' ||
      field === 'HASH'
    ) {
      valueType = FingerprintValueType.Hash;
    }

    const fingerprintValue = {
      value: fp.value,
      valueType,
    };

    // Create the appropriate fingerprint type based on signal type
    if (generated.signalType === SignalType.Transaction) {
      return {
        __typename: 'TransactionFingerprint' as const,
        txField: field,
        field: field as TransactionField,
        operation,
        value: fingerprintValue,
      } as unknown as TransactionFingerprint;
    } else if (generated.signalType === SignalType.Log) {
      return {
        __typename: 'LogFingerprint' as const,
        field: field as LogField,
        operation,
        value: fingerprintValue,
      } as unknown as LogFingerprint;
    } else {
      return {
        __typename: 'BlockFingerprint' as const,
        field: field as BlockField,
        operation,
        value: fingerprintValue,
      } as unknown as BlockFingerprint;
    }
  });
};

const SignalCreate: React.FC = () => {
  const navigate = useNavigate();
  const [createSignal, { loading }] = useCreateSignalMutation();

  // Mode state - default to AI
  const [mode, setMode] = useState<CreateMode>('ai');

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [signalType, setSignalType] = useState<SignalType>(
    SignalType.Transaction,
  );
  const [fingerprints, setFingerprints] = useState<Fingerprint[]>([]);
  const [error, setError] = useState<string | null>(null);

  // AI metadata state
  const [aiMetadata, setAiMetadata] = useState<{
    confidence: number;
    reasoning: string;
  } | null>(null);

  // Create a mock signal object for the SignalEditor
  const signal = {
    id: '',
    name,
    signalType,
    fingerprints,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const handleSignalChange = (updatedSignal: typeof signal) => {
    setFingerprints(updatedSignal.fingerprints);
  };

  // Handle AI generation result
  const handleAIGenerated = (result: GeneratedSignalResult) => {
    setName(result.name);
    setDescription(result.description);
    setSignalType(result.signalType);
    setFingerprints(convertAIFingerprints(result));
    setAiMetadata({
      confidence: result.confidence,
      reasoning: result.reasoning,
    });
    setMode('manual'); // Switch to form to review/edit
  };

  // Switch to manual mode
  const handleSwitchToManual = () => {
    setMode('manual');
  };

  // Switch back to AI mode
  const handleSwitchToAI = () => {
    setMode('ai');
    setAiMetadata(null);
  };

  // Check if the signal type requires fingerprints
  const requiresFingerprints =
    signalType === SignalType.Transaction || signalType === SignalType.Log;

  // Transaction signals require at least one fingerprint
  const canCreate =
    name.trim() && (!requiresFingerprints || fingerprints.length > 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (requiresFingerprints && fingerprints.length === 0) {
      setError('At least one condition is required for this signal type');
      return;
    }

    try {
      // Convert fingerprints to the input format expected by the backend
      const fingerprintInputs: TransactionFingerprintInput[] = fingerprints.map(
        (fp) => {
          const txFp = fp as TransactionFingerprint;
          return {
            field: (txFp.txField || txFp.field) as unknown as TransactionField,
            operator: txFp.operation,
            value: txFp.value?.value,
          };
        },
      );

      const result = await createSignal({
        variables: {
          input: {
            name: name.trim(),
            description: description.trim(),
            signalType,
            fingerprints:
              fingerprintInputs.length > 0 ? fingerprintInputs : undefined,
          },
        },
        refetchQueries: [
          { query: GetSignalsDocument, variables: { first: 50 } },
        ],
      });

      if (result.data?.createSignal) {
        navigate(`/signals/${result.data.createSignal.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create signal');
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'green';
    if (confidence >= 0.6) return 'yellow';
    return 'orange';
  };

  return (
    <Box>
      <Box pb="4" px="1">
        <Flex align="center" gap="3">
          <IconButton
            variant="ghost"
            color="gray"
            onClick={() => navigate('/signals')}
          >
            <ArrowLeftIcon width={20} height={20} />
          </IconButton>
          <Box
            style={{
              padding: '8px',
              borderRadius: '8px',
              backgroundColor: 'var(--purple-3)',
              color: 'var(--purple-11)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MixerHorizontalIcon width={24} height={24} />
          </Box>
          <Box>
            <Heading as="h1" size="5" weight="bold">
              Create Signal
            </Heading>
            <Text size="2" color="gray">
              Define a new pattern for monitoring blockchain activity
            </Text>
          </Box>
        </Flex>
      </Box>

      {/* Mode Switcher */}
      <Box mb="4">
        <SegmentedControl.Root
          value={mode}
          onValueChange={(value) => setMode(value as CreateMode)}
        >
          <SegmentedControl.Item value="ai">
            <Flex align="center" gap="1">
              <MagicWandIcon width={14} height={14} />
              AI Generate
            </Flex>
          </SegmentedControl.Item>
          <SegmentedControl.Item value="manual">
            <Flex align="center" gap="1">
              <Pencil1Icon width={14} height={14} />
              Manual
            </Flex>
          </SegmentedControl.Item>
        </SegmentedControl.Root>
      </Box>

      {mode === 'ai' ? (
        <AISignalPrompt
          onGenerated={handleAIGenerated}
          onSwitchToManual={handleSwitchToManual}
        />
      ) : (
        <Card size="2">
          <form onSubmit={handleSubmit}>
            <Flex direction="column" gap="4">
              {/* AI Metadata Banner */}
              {aiMetadata && (
                <Callout.Root color="purple" size="1">
                  <Callout.Icon>
                    <MagicWandIcon />
                  </Callout.Icon>
                  <Callout.Text>
                    <Flex direction="column" gap="1">
                      <Flex align="center" gap="2">
                        <Text weight="medium">AI Generated</Text>
                        <Badge color={getConfidenceColor(aiMetadata.confidence)}>
                          {Math.round(aiMetadata.confidence * 100)}% confidence
                        </Badge>
                      </Flex>
                      <Text size="1" color="gray">
                        {aiMetadata.reasoning}
                      </Text>
                      <Button
                        variant="ghost"
                        size="1"
                        onClick={handleSwitchToAI}
                        style={{ alignSelf: 'flex-start', marginTop: '4px' }}
                      >
                        <MagicWandIcon width={12} height={12} />
                        Try a different prompt
                      </Button>
                    </Flex>
                  </Callout.Text>
                </Callout.Root>
              )}

              <Box>
                <Text as="label" size="2" weight="medium" mb="1">
                  Name
                </Text>
                <TextField.Root
                  placeholder="Enter signal name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Box>

              <Box>
                <Text as="label" size="2" weight="medium" mb="1">
                  Description
                </Text>
                <TextField.Root
                  placeholder="Enter signal description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Box>

              <Box>
                <Text as="label" size="2" weight="medium" mb="1">
                  Signal Type
                </Text>
                <Select.Root
                  value={signalType}
                  onValueChange={(value) => {
                    setSignalType(value as SignalType);
                    // Clear fingerprints when changing signal type
                    setFingerprints([]);
                  }}
                >
                  <Select.Trigger style={{ width: '100%' }} />
                  <Select.Content>
                    <Select.Item value={SignalType.Block}>Block</Select.Item>
                    <Select.Item value={SignalType.Network}>Network</Select.Item>
                    <Select.Item value={SignalType.Transaction}>
                      Transaction
                    </Select.Item>
                    <Select.Item value={SignalType.Log}>Log</Select.Item>
                  </Select.Content>
                </Select.Root>
                <Text size="1" color="gray" mt="1">
                  The type of blockchain data this signal monitors
                </Text>
              </Box>

              {requiresFingerprints && (
                <Callout.Root color="blue" size="1">
                  <Callout.Icon>
                    <InfoCircledIcon />
                  </Callout.Icon>
                  <Callout.Text>
                    {signalType === SignalType.Transaction
                      ? 'Transaction signals require at least one condition to filter transactions.'
                      : 'Log signals require at least one condition to filter event logs.'}
                  </Callout.Text>
                </Callout.Root>
              )}

              <SignalEditor
                signal={signal}
                onChange={handleSignalChange}
                showHeader={true}
              />

              {error && (
                <Text color="red" size="2">
                  {error}
                </Text>
              )}

              <Flex gap="3" justify="end" mt="2">
                <Button
                  type="button"
                  variant="soft"
                  color="gray"
                  onClick={() => navigate('/signals')}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading || !canCreate}>
                  {loading ? 'Creating...' : 'Create Signal'}
                </Button>
              </Flex>
            </Flex>
          </form>
        </Card>
      )}
    </Box>
  );
};

export default SignalCreate;
